import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { Localized } from '@lib/glossary/types'

export interface ComplianceRequirement {
  requirement: Localized
  detail: Localized
  capability: Localized
  productHref: string
  productName: string
}

export interface ComplianceFaq {
  q: Localized
  a: Localized
}

export interface CompliancePageContent {
  /** Route without locale prefix, e.g. '/compliance/kvkk' */
  path: string
  /** When true, emits EN/TR hreflang alternates (page fully translated). */
  bilingual: boolean
  /**
   * TR-only page: rendered in Turkish with a /tr canonical and no hreflang
   * alternates. The page's getStaticProps must return notFound for other
   * locales (yerli/kamu/bddk pattern) and the bare EN path goes into the
   * sitemap exclude list.
   */
  trOnly?: boolean
  seoTitle: Localized
  seoDescription: Localized
  heroEyebrow: Localized
  heroTitle: Localized
  heroBody: Localized
  contextHeading: Localized
  contextBody: Localized
  requirementsHeading: Localized
  requirementsBody: Localized
  requirements: ComplianceRequirement[]
  faqs: ComplianceFaq[]
}

const ui = {
  requirementLabel: { en: 'Requirement', tr: 'Gereklilik' },
  capabilityLabel: { en: 'How Monofor helps', tr: 'Monofor nasıl yardımcı olur' },
  faqHeading: { en: 'Common questions', tr: 'Sık sorulan sorular' },
  ctaTrial: { en: 'Start free trial →', tr: 'Ücretsiz deneme başlat →' },
  ctaExperts: { en: 'Talk with our experts', tr: 'Uzmanlarımızla görüşün' },
  disclaimer: {
    en: 'This page is general product information, not legal or compliance advice. Work with your legal and audit teams to determine how the framework applies to your organization.',
    tr: 'Bu sayfa genel ürün bilgisidir; hukuki veya uyum danışmanlığı değildir. Çerçevenin kuruluşunuza nasıl uygulanacağını hukuk ve denetim ekiplerinizle birlikte değerlendirin.',
  },
}

function pick(value: Localized, locale: string): string {
  return locale === 'tr' ? value.tr || value.en : value.en
}

interface Props {
  /** Optional gated-asset section rendered just before the final CTA. */
  leadMagnet?: React.ReactNode
  content: CompliancePageContent
  locale: string
}

export default function CompliancePage({ content, locale, leadMagnet }: Props) {
  const usedLocale = content.trOnly ? 'tr' : content.bilingual ? locale : 'en'
  const basePath = content.trOnly || (content.bilingual && locale === 'tr') ? '/tr' : ''
  const p = (v: Localized) => pick(v, usedLocale)

  return (
    <Layout>
      <Seo
        title={p(content.seoTitle)}
        description={p(content.seoDescription)}
        path={`${basePath}${content.path}`}
      />
      {content.bilingual && (
        <Head>
          <link rel="alternate" hrefLang="en" href={`${SITE_URL}${content.path}`} />
          <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr${content.path}`} />
          <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${content.path}`} />
        </Head>
      )}
      <JsonLd
        id="compliance-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: content.faqs.map((f) => ({
            '@type': 'Question',
            name: p(f.q),
            acceptedAnswer: { '@type': 'Answer', text: p(f.a) },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{p(content.heroEyebrow)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {p(content.heroTitle)}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {p(content.heroBody)}
                </Text>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Button href="/try/now" variant="primary" size="lg">
                  {p(ui.ctaTrial)}
                </Button>
                <Button href="/contact" variant="outline" size="lg">
                  {p(ui.ctaExperts)}
                </Button>
              </div>
            </div>
          </Container>
        </Section>

        {/* Context */}
        <Section tone="subtle" density="compact">
          <Container size="md">
            <Heading as="h2" size="display-md">
              {p(content.contextHeading)}
            </Heading>
            {p(content.contextBody)
              .split('\n\n')
              .map((paragraph, i) => (
                <p
                  key={i}
                  className="mt-5 text-body-lg leading-relaxed text-ink-secondary"
                >
                  {paragraph}
                </p>
              ))}
          </Container>
        </Section>

        {/* Requirements mapping */}
        <Section tone="default" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{p(ui.requirementLabel)} → Monofor</Eyebrow>
              <Heading as="h2" size="display-lg" className="mt-5">
                {p(content.requirementsHeading)}
              </Heading>
              <Text size="xl" tone="secondary" className="mt-6">
                {p(content.requirementsBody)}
              </Text>
            </div>
            <div className="mt-12 space-y-4">
              {content.requirements.map((req) => (
                <div
                  key={req.requirement.en}
                  className="grid gap-6 rounded-3xl border border-line-subtle bg-surface p-6 shadow-card lg:grid-cols-[1fr_1.2fr] lg:gap-10 lg:p-8"
                >
                  <div>
                    <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                      {p(ui.requirementLabel)}
                    </div>
                    <div className="mt-2 text-body-lg font-medium text-ink-primary">
                      {p(req.requirement)}
                    </div>
                    <Text size="sm" tone="tertiary" className="mt-2">
                      {p(req.detail)}
                    </Text>
                  </div>
                  <div className="border-t border-line-subtle pt-5 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10">
                    <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-accent-700">
                      {p(ui.capabilityLabel)}
                    </div>
                    <Text size="md" tone="secondary" className="mt-2">
                      {p(req.capability)}
                    </Text>
                    <Link
                      href={req.productHref}
                      className="mt-3 inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-700 transition hover:translate-x-0.5"
                    >
                      {req.productName}
                      <span aria-hidden>→</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 max-w-3xl text-body-sm text-ink-tertiary">
              {p(ui.disclaimer)}
            </div>
          </Container>
        </Section>

        <TrustBand />

        {/* FAQ */}
        <Section tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="brand">FAQ</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {p(ui.faqHeading)}
            </Heading>
            <dl className="mt-12 space-y-4">
              {content.faqs.map((faq) => (
                <div
                  key={faq.q.en}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {p(faq.q)}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {p(faq.a)}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        {leadMagnet}
        <FinalCta />
      </main>
    </Layout>
  )
}
